import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import ListItemText from "@mui/material/ListItemText";
import { alpha, styled } from "@mui/material/styles";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemButton from "@mui/material/ListItemButton";
import { ListItemButtonProps } from "@mui/material/ListItemButton";
import RouterLink from "src/routes/RouterLink";
import Iconify from "src/components/Iconify";

type StyledItemProps = Omit<NavItemProps, "item"> & {
  config: NavConfigProps;
};

export const StyledItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "active",
})<StyledItemProps>(({ active, depth, config, theme }) => {
  const subItem = depth !== 1;

  const deepSubItem = depth > 2;

  const activeStyles = {
    root: {
      color: "#667eea",
      background:
        "linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)",
      border: "1px solid rgba(102, 126, 234, 0.3)",
      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.2)",
      transform: "translateX(4px)",
      "&:hover": {
        background:
          "linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)",
        boxShadow: "0 6px 16px rgba(102, 126, 234, 0.3)",
        transform: "translateX(6px)",
      },
    },
    sub: {
      color: theme.palette.text.primary,
      background:
        "linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)",
      border: "1px solid rgba(102, 126, 234, 0.2)",
      "&:hover": {
        background:
          "linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(118, 75, 162, 0.12) 100%)",
        transform: "translateX(2px)",
      },
    },
  };

  return {
    // Root item
    padding: config.itemPadding,
    marginBottom: config.itemGap,
    borderRadius: config.itemRadius,
    minHeight: config.itemRootHeight,
    color: theme.palette.text.secondary,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",

    "&:hover": {
      background:
        "linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)",
      transform: "translateX(2px)",
    },

    // Active root item
    ...(active && {
      ...activeStyles.root,
    }),

    ...(subItem && {
      minHeight: config.itemSubHeight,
      ...(active && {
        ...activeStyles.sub,
      }),
    }),

    // Deep sub item
    ...(deepSubItem && {
      paddingLeft: theme.spacing(depth),
    }),

    // アニメーション効果
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "4px",
      height: "100%",
      background: "linear-gradient(180deg, #667eea 0%, #764ba2 100%)",
      opacity: active ? 1 : 0,
      transition: "opacity 0.3s ease",
    },
  };
});
type StyledIconProps = {
  size?: number;
};

export const StyledIcon = styled(ListItemIcon)<StyledIconProps>(({ size }) => ({
  width: size,
  height: size,
  alignItems: "center",
  justifyContent: "center",
}));

type StyledDotIconProps = {
  active?: boolean;
};

export const StyledDotIcon = styled("span")<StyledDotIconProps>(
  ({ active, theme }) => ({
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: active
      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      : theme.palette.text.disabled,
    transition: theme.transitions.create(["transform", "background"], {
      duration: theme.transitions.duration.shorter,
    }),
    boxShadow: active ? "0 2px 8px rgba(102, 126, 234, 0.4)" : "none",
    ...(active && {
      transform: "scale(1.5)",
    }),
  })
);

export type NavConfigProps = {
  hiddenLabel?: boolean;
  itemGap?: number;
  iconSize?: number;
  itemRadius?: number;
  itemPadding?: string;
  currentRole?: string;
  itemSubHeight?: number;
  itemRootHeight?: number;
};

export type NavItemProps = ListItemButtonProps & {
  item: NavListProps;
  depth: number;
  open?: boolean;
  active: boolean;
  externalLink?: boolean;
};

export type NavListProps = {
  title: string;
  path: string;
  icon?: React.ReactElement;
  info?: React.ReactElement;
  caption?: string;
  disabled?: boolean;
  roles?: string[];
  children?: any;
};

type Props = NavItemProps & {
  config: NavConfigProps;
};

export default function NavItem({
  item,
  open,
  depth,
  active,
  config,
  externalLink,
  ...other
}: Props) {
  const { title, path, icon, info, children, disabled, caption, roles } = item;

  const subItem = depth !== 1;

  const renderContent = (
    <StyledItem
      disableGutters
      disabled={disabled}
      active={active}
      depth={depth}
      config={config}
      {...other}
    >
      <>
        {icon && <StyledIcon size={config.iconSize}>{icon}</StyledIcon>}

        {subItem && (
          <StyledIcon size={config.iconSize}>
            <StyledDotIcon active={active} />
          </StyledIcon>
        )}
      </>

      {!(config.hiddenLabel && !subItem) && (
        <ListItemText
          primary={title}
          secondary={
            caption ? (
              <Tooltip title={caption} placement="top-start">
                <span>{caption}</span>
              </Tooltip>
            ) : null
          }
          primaryTypographyProps={{
            noWrap: true,
            typography: "body2",
            textTransform: "capitalize",
            fontWeight: active ? "fontWeightSemiBold" : "fontWeightMedium",
          }}
          secondaryTypographyProps={{
            noWrap: true,
            component: "span",
            typography: "caption",
            color: "text.disabled",
          }}
        />
      )}

      {info && (
        <Box component="span" sx={{ ml: 1, lineHeight: 0 }}>
          {info}
        </Box>
      )}

      {!!children && (
        <Iconify
          width={16}
          icon={
            open ? "eva:arrow-ios-downward-fill" : "eva:arrow-ios-forward-fill"
          }
          sx={{ ml: 1, flexShrink: 0 }}
        />
      )}
    </StyledItem>
  );

  // Hidden item by role
  if (roles && !roles.includes(`${config.currentRole}`)) {
    return null;
  }

  // External link
  if (externalLink)
    return (
      <Link
        href={path}
        target="_blank"
        rel="noopener"
        underline="none"
        color="inherit"
        sx={{
          ...(disabled && {
            cursor: "default",
          }),
        }}
      >
        {renderContent}
      </Link>
    );

  // Has child
  if (children) {
    return renderContent;
  }

  // Default
  return (
    <Link
      component={RouterLink}
      href={path}
      underline="none"
      color="inherit"
      sx={{
        ...(disabled && {
          cursor: "default",
        }),
      }}
    >
      {renderContent}
    </Link>
  );
}
